<template>
  <div class="w-full my-6">
    <div class="flex flex-col gap-y-3 text-primary">
      <h2 class="text-2xl ltr:block rtl:hidden">
        {{ label }}
      </h2>
      <div class="w-full py-2 flex justify-between">
        <div
          :key="key"
          v-for="(step, key) in steps"
          class="w-40 flex flex-col gap-y-2 items-center"
        >
          <div class="w-16 md:w-32 relative aspect-square">
            <div
              class="absolute -top-1 -end-1 md:top-0 md:end-1 rounded-full border md:border-2 border-white bg-primary text-white w-7 md:w-8 aspect-square flex justify-center items-center"
            >
              {{ key + 1 }}
            </div>
            <div
              class="w-16 md:w-32 flex justify-center items-center rounded-full aspect-square border-[3px] border-primary"
            >
              <i :class="step.icon" class="text-xl md:text-[3rem]"></i>
            </div>
          </div>
          <span class="text-xs md:text-lg text-center">{{ $t(step.label) }}</span>
        </div>
      </div>
    </div>
    <!-- steps circle label -->
    <div class="flex flex-col gap-y-4">
      <CollapsibleSection :header-label="$t('serviceProcedures')">
        <div class="flex flex-col gap-y-2 divide-y divide-brand-gray-250">
          <div class="pt-2 flex flex-col md:flex-row gap-x-3">
            <h3 class="w-full md:w-1/4">{{ $t('sc.serviceProcedures.webPortalMobile.title') }}</h3>
            <ul class="">
              <li
                :key="key"
                v-for="key in [0, 1, 2, 3]"
                class="flex gap-x-2 text-brand-black-secondary"
              >
                <faChevronRight class="pt-0.5 w-1.5 fill-brand-black-secondary" />
                {{ $t(`sc.serviceProcedures.webPortalMobile.ul.li${key + 1}`) }}
              </li>
            </ul>
          </div>
          <div class="pt-2 flex flex-col md:flex-row gap-x-3">
            <h3 class="w-full md:w-1/4">{{ $t('sc.serviceProcedures.serviceCenters.title') }}</h3>
            <ul class="">
              <li
                :key="key"
                v-for="key in [0, 1, 2, 3]"
                class="flex gap-x-2 text-brand-black-secondary"
              >
                <faChevronRight class="pt-0.5 w-1.5 fill-brand-black-secondary" />
                {{ $t(`sc.serviceProcedures.serviceCenters.ul.li${key + 1}`) }}
              </li>
            </ul>
          </div>
          <div class="pt-2 flex flex-col md:flex-row gap-x-3">
            <h3 class="w-full md:w-1/4">{{ $t('sc.serviceProcedures.typingCenters.title') }}</h3>
            <ul class="">
              <li
                :key="key"
                v-for="key in [0, 1, 2]"
                class="flex items-start gap-x-2 text-brand-black-secondary"
              >
                <faChevronRight class="pt-1.5 w-1.5 fill-brand-black-secondary" />
                {{ $t(`sc.serviceProcedures.typingCenters.ul.li${key + 1}`) }}
              </li>
            </ul>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection :header-label="$t('serviceDocumentsDescription')">
        <div class="flex flex-col gap-y-3 text-brand-black-secondary">
          <div class="flex flex-col md:flex-row md:flex-wrap">
            <div
              :key="key"
              v-for="(att, key) in attachmentTypes"
              class="w-full md:w-1/3 flex items-start gap-2"
            >
              <faChevronRight class="pt-1.5 w-1.5 fill-brand-black-secondary" />
              <span class="">
                {{ att.localizedName }}
              </span>
            </div>
          </div>
          <p>{{ $t('requiredDocumentsNote') }}</p>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        v-if="(service?.fees?.result?.length || []) > 0"
        :header-label="$t('feesInformation')"
      >
        <div class="w-full flex flex-col items-start gap-y-3">
          <table class="w-full md:w-1/2 table-auto border-collapse border ltr:font-sans">
            <tr :key="key" v-for="(row, key) in service.fees.result">
              <td class="px-4 py-2 border border-gray-100 ltr:font-sans text-sm">
                <span class="ltr:hidden rtl:block">{{ row.arabicName }}</span>
                <span class="ltr:block rtl:hidden">{{ row.englishName }}</span>
              </td>
              <td class="px-4 py-2 border border-gray-100 ltr:font-sans text-end">
                {{ intlNumberFormat.format(row.amount) }}
              </td>
            </tr>
          </table>
          <p>{{ $t('requiredDocumentsNote') }}</p>
        </div>
      </CollapsibleSection>
    </div>
  </div>
</template>

<script setup>
import CollapsibleSection from '@/components/Ui/CollapsibleSection.vue'
import { computed, onMounted, ref } from 'vue'
import { useGlobalStore } from '@/stores/global'
import { useRoute } from 'vue-router'
import faChevronRight from '@/assets/icons/faChevronRight.svg?component'
import apiHelperService from '@/services'

const props = defineProps({
  label: {
    type: String,
    default: ''
  }
})

const intlNumberFormat = Intl.NumberFormat('AE-en', { style: 'currency', currency: 'AED' })
const globalStore = useGlobalStore()
const route = useRoute()
const attachmentTypes = ref([])
const service = ref()

const steps = [
  {
    icon: 'ica-custom-fonts-draft o-service-icon-content',
    label: 'fillInformation'
  },
  {
    icon: 'ica-custom-fonts-passport o-service-icon-content',
    label: 'TheAttachments'
  },
  {
    icon: 'ica-custom-fonts-transactions o-service-icon-content',
    label: 'theFees'
  },
  {
    icon: 'ica-custom-fonts-stamp o-service-icon-content',
    label: 'serviceRate'
  },
  {
    icon: 'ica-custom-fonts-stamp-2 o-service-icon-content',
    label: 'EmailResived'
  }
]

const serviceId = computed(() => route.query.serviceTransactionId)

const fetchAttachments = async () => {
  const response = await apiHelperService.get(
    `/api/lookup/service/${serviceId.value}/adminRegion/${route.query?.administrativeRegionId}/attachmentTypes`
  )
  attachmentTypes.value = response.filter((att) => att.id === 835 || att.id === 837)
}

onMounted(async () => {
  service.value = await globalStore.fetchServiceCard(serviceId.value)
  await fetchAttachments()
})
</script>

<style></style>
